/** @file pictureQuadOf: a drawn quad and a point on it moved onto the pixels the picture fills. */
import { pictureRound } from './pictureRound';
import { quadPoint } from './quadPoint';

type P2 = [number, number];

/** The quad `[tl, tr, br, bl]` as the picture shows it and `pivot` carried along with it (target px). */
export type PictureQuad = { corners: [P2, P2, P2, P2]; pivot: P2 };

/** `(s, t)` of `p` on the parallelogram `c0 + s (c1 - c0) + t (c3 - c0)`; `(0, 0)` for a degenerate quad. */
function paramOf(c: readonly (readonly [number, number])[], p: readonly [number, number]): [number, number] {
  const z: readonly [number, number] = [0, 0];
  const [o, x, y] = [c[0] ?? z, c[1] ?? z, c[3] ?? z];
  const a: P2 = [x[0] - o[0], x[1] - o[1]];
  const b: P2 = [y[0] - o[0], y[1] - o[1]];
  const d: P2 = [p[0] - o[0], p[1] - o[1]];
  const det = a[0] * b[1] - a[1] * b[0];
  if (Math.abs(det) < 1e-12) return [0, 0];
  return [(d[0] * b[1] - d[1] * b[0]) / det, (a[0] * d[1] - a[1] * d[0]) / det];
}

/**
 * Every corner coordinate through `pictureRound.edge` (the rim of the pixels the quad lights: exact for the upright
 * quads of `none` / `screen` parts, the nearest pixel corners for a turned one), and `pivot` at the same quad
 * parameters on the moved quad (so it keeps its place on the picture; the corners' mean without one).
 */
export function pictureQuadOf(c: readonly (readonly [number, number])[], pivot?: readonly [number, number]): PictureQuad {
  const at = (i: number): P2 => {
    const q = c[i] ?? [0, 0];
    return [pictureRound.edge(q[0]), pictureRound.edge(q[1])];
  };
  const corners: [P2, P2, P2, P2] = [at(0), at(1), at(2), at(3)];
  const [s, t] = pivot === undefined ? [0.5, 0.5] : paramOf(c, pivot);
  return { corners, pivot: quadPoint(corners, s, t) };
}
